import React from 'react';

import knowYourStoryImage from '@backrs_ui/grow-ui/src/assets/images/extracurriculars-guide/know-your-story.jpg';
import runningLogImage from '@backrs_ui/grow-ui/src/assets/images/extracurriculars-guide/running-log.png';
import { GUIDE_TEXT_MAX_WIDTH } from '@backrs_ui/grow-ui/src/components/GuidePage';
import type {
  ExtracurricularsGuideBeforeYouApply,
  ExtracurricularsGuideBeforeYouApplyCardId,
} from '@backrs_ui/grow-ui/src/pages/ExtracurricularsGuide/content/types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type BeforeYouApplySectionProps = Readonly<{
  content: ExtracurricularsGuideBeforeYouApply;
}>;

const CARD_IMAGES: Readonly<Record<ExtracurricularsGuideBeforeYouApplyCardId, string>> = {
  'running-log': runningLogImage,
  'know-your-story': knowYourStoryImage,
};

export const BeforeYouApplySection: React.FC<BeforeYouApplySectionProps> = ({ content }) => (
  <Box sx={containerSx}>
    {content.cards.map((card) => (
      <Box key={card.id} sx={cardSx}>
        <Box sx={imageWrapperSx}>
          <Box component="img" src={CARD_IMAGES[card.id]} alt="" sx={imageSx} />
        </Box>
        <Box sx={textSx}>
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {card.title}
          </Typography>
          {card.body.map((paragraph) => (
            <Typography key={paragraph} variant="text-md-regular" sx={bodySx}>
              {paragraph}
            </Typography>
          ))}
          {card.items.length > 0 && (
            <Box sx={listSx}>
              {card.items.map((item) => (
                <Typography key={item.label} variant="text-md-regular" sx={itemSx}>
                  <Box component="span" sx={labelSx}>
                    {item.label}
                  </Box>
                  {` — ${item.text}`}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 720px)': {
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'start',
  },
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  overflow: 'hidden',
});

const textSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  p: backrs.spacing['spacing-3xl'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const itemSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const labelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-semibold'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const imageWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '200px',
  flexShrink: 0,
  overflow: 'hidden',
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
});

const imageSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};
